import { getIdForPart, partsAreTheSame } from "../../../utils/parts";
import { FootprintCell, HorizontalRow, TextArea } from "./PartsTableStyles";
import { SelectableTableRow, TableCell } from "../../../constants/Styles";
import RadioButton from "../../common/RadioButton";
import { Part, PartProperty } from "../../../reducers/models/parts";
import { displayProperty } from "../../../utils/properties";
import { formatSmallCurrency } from "../../../utils/valueFormatting";
import FlashPCBTooltip from "../../common/FlashPCBTooltip";
import Icon from "../../common/Icon";
import { ParameterAggregate } from "../../../reducers/componentSearch";
import React, { useEffect, useState } from "react";
import Button from "../../common/Button";
import { ButtonVariants } from "../../common/Button/constants";
import PartDrawer, { PartDrawerPageMode } from "../PartDrawer";
import { useRefreshSearch } from "../../../utils/hooks/partSearch";
import PartFootprintSummary from "../../FootprintSummary";

function formatPartPrice(part: Part) {
  if (part.price_info === null) {
    return "";
  }
  const formattedPrice = formatSmallCurrency(
    part.price_info.unit_price,
    part.price_info.currency,
  );
  if (part.price_info.quantity === 1) {
    return formattedPrice;
  }
  return (
    <TextArea>
      {formattedPrice}
      <FlashPCBTooltip
        content={`This part has a minimum order quantity of ${part.price_info.quantity.toLocaleString()}`}
      >
        <Icon
          src="/icons/alert_yellow.svg"
          alt="alert"
          styleProps={{ marginLeft: "0.5rem" }}
        />
      </FlashPCBTooltip>
    </TextArea>
  );
}

export default function PartRow({
  part,
  selectedPartId,
  setSelectedPartId,
  warnOnNoInventory,
  filteredAggregatesForTable,
  isAdminOrEngineer,
}: {
  part: Part;
  selectedPartId?: string | null;
  setSelectedPartId: (part_id: string | null) => void;
  warnOnNoInventory: boolean;
  filteredAggregatesForTable: ParameterAggregate[];
  isAdminOrEngineer: boolean;
}) {
  const partId = getIdForPart(part);
  const partIsSelected = partsAreTheSame(
    [selectedPartId as string | null],
    [part.id, part.octopart_id],
  );
  const showRadioButtons = selectedPartId !== undefined;
  const [partToEdit, setPartToEdit] = useState<string | null>(null);
  const [isUpdated, setIsUpdated] = useState(false);
  const refreshSearch = useRefreshSearch();

  useEffect(() => {
    if (isUpdated) {
      refreshSearch();
      setIsUpdated(false);
    }
  }, [isUpdated, refreshSearch]);

  const selectPart = () => setSelectedPartId(partId);

  const closeDrawer = () => {
    setPartToEdit(null);
  };

  return (
    <SelectableTableRow
      warn={warnOnNoInventory && part.inventory_level === 0}
      selected={partIsSelected}
      onClick={selectPart}
    >
      {showRadioButtons && (
        <TableCell borderRight onClick={selectPart}>
          <RadioButton
            onChange={selectPart}
            checked={partIsSelected}
            id={partId}
            name={`${part.name} part select`}
          />
        </TableCell>
      )}
      <TableCell>
        <FootprintCell>
          <div onClick={(e) => e.stopPropagation()}>
            <PartFootprintSummary part={part} />
          </div>
          <div>{part.name}</div>
        </FootprintCell>
      </TableCell>
      <TableCell>{part.manufacturer}</TableCell>
      <TableCell>{part.short_description}</TableCell>
      <TableCell>{formatPartPrice(part)}</TableCell>
      <TableCell>
        <HorizontalRow>
          {part.inventory_level.toLocaleString()}{" "}
          {isAdminOrEngineer && (
            <Button
              label={"Edit"}
              variant={ButtonVariants.SECONDARY}
              isSmallSize
              onClick={() => setPartToEdit(part.id)}
            />
          )}
        </HorizontalRow>
        <PartDrawer
          partId={partToEdit}
          setIsUpdated={setIsUpdated}
          onClose={closeDrawer}
          initialPageMode={PartDrawerPageMode.INVENTORY}
        />
      </TableCell>
      <TableCell>{part.physical_inventory_level.toLocaleString()}</TableCell>
      {isAdminOrEngineer && (
        <>
          <TableCell>
            {part.total_last_counted_amount.toLocaleString()}
          </TableCell>
          <TableCell>
            {part.total_active_allocations.toLocaleString()}
          </TableCell>
          <TableCell>{part.total_spent_allocations.toLocaleString()}</TableCell>
        </>
      )}
      {filteredAggregatesForTable.map((aggregate) => {
        const partProperty = part.properties.find(
          (partProperty: PartProperty) =>
            partProperty.parameter.id == aggregate.parameter.id,
        );

        return (
          <TableCell key={aggregate.parameter.id}>
            {partProperty ? displayProperty(partProperty) : "-"}
          </TableCell>
        );
      })}
    </SelectableTableRow>
  );
}
